import React, { useEffect, useState } from "react";
import "@/App.css";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import ApiKeyGate from "@/components/ApiKeyGate";
import Header from "@/components/Header";
import BgTexture from "@/components/BgTexture";
import Footer from "@/components/Footer";
import PartnershipPortal from "@/tabs/PartnershipPortal";
import CreateOffer from "@/tabs/CreateOffer";
import CreateContent from "@/tabs/CreateContent";
import { Handshake, Package2, Clapperboard } from "lucide-react";

const KEY_STORAGE = "buddypass_openai_key";

function App() {
  const [apiKey, setApiKey] = useState(null);
  const [tab, setTab] = useState("partnership");
  const [offerData, setOfferData] = useState(null);
  // Handoff payload from Create Your Asset → Create Your Content (audience DNA,
  // emotional triggers, vocabulary). Carries over without forcing a redesign.
  const [contentHandoff, setContentHandoff] = useState(null);

  // Helper: jump to the Content tab with a pre-filled context bundle.
  const goToContent = (payload = null) => {
    if (payload) setContentHandoff(payload);
    setTab("content");
  };

  useEffect(() => {
    document.documentElement.classList.add("dark");
    const k = localStorage.getItem(KEY_STORAGE);
    if (k) setApiKey(k);
  }, []);

  const handleClearKey = () => {
    localStorage.removeItem(KEY_STORAGE);
    setApiKey(null);
  };

  if (!apiKey) {
    return (
      <div className="App dark relative overflow-hidden">
        <BgTexture />
        <ApiKeyGate onSave={setApiKey} />
        <Toaster
          theme="dark"
          position="bottom-center"
          toastOptions={{
            style: {
              background: "#18181b",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="App dark relative min-h-screen">
      <BgTexture />
      <Header onClearKey={handleClearKey} apiKey={apiKey} />
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            data-testid="main-tabs"
            className="inline-flex h-auto rounded-full border border-white/10 bg-zinc-900/60 p-1 gap-1 mb-10"
          >
            <TabsTrigger
              value="partnership"
              data-testid="tab-partnership"
              className="tab-pill tab-pill-partner rounded-full px-4 sm:px-5 h-10 text-xs sm:text-sm font-semibold data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white transition-all"
            >
              <Handshake className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              <span className="hidden xs:inline">Partnership Portal</span>
              <span className="xs:hidden">Partner</span>
            </TabsTrigger>
            <TabsTrigger
              value="offer"
              data-testid="tab-offer"
              className="tab-pill tab-pill-offer rounded-full px-4 sm:px-5 h-10 text-xs sm:text-sm font-semibold data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white transition-all"
            >
              <Package2 className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              Create Your Asset
            </TabsTrigger>
            <TabsTrigger
              value="content"
              data-testid="tab-content"
              className="tab-pill tab-pill-content rounded-full px-4 sm:px-5 h-10 text-xs sm:text-sm font-semibold data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white transition-all"
            >
              <Clapperboard className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              Create Your Content
            </TabsTrigger>
          </TabsList>

          <TabsContent value="partnership" className="focus-visible:outline-none">
            <PartnershipPortal apiKey={apiKey} />
          </TabsContent>
          <TabsContent value="offer" className="focus-visible:outline-none">
            <CreateOffer
              apiKey={apiKey}
              offerData={offerData}
              setOfferData={setOfferData}
              onCreateContent={goToContent}
            />
          </TabsContent>
          <TabsContent value="content" className="focus-visible:outline-none">
            <CreateContent
              apiKey={apiKey}
              offerData={offerData}
              handoff={contentHandoff}
              clearHandoff={() => setContentHandoff(null)}
            />
          </TabsContent>
        </Tabs>
      </main>

      <footer className="relative z-10">
        <Footer />
      </footer>

      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#18181b",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
}

export default App;
